import { useParams, useNavigate } from 'react-router-dom';
import { Card, Statistic, Row, Col, Button, Spin, Empty } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { strategyApi } from '@tradermind/shared/api';

export default function StrategyStatsPage() {
  const { t } = useTranslation(['strategy', 'common']);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: strategyData, isLoading: strategyLoading } = useQuery({
    queryKey: ['strategy', id],
    queryFn: () => strategyApi.getStrategyById(Number(id)),
    enabled: !!id,
  });

  const { data: statsData, isLoading: statsLoading } = useQuery({
    queryKey: ['strategy-stats', id],
    queryFn: () => strategyApi.getStrategyStats(Number(id)),
    enabled: !!id,
  });

  const strategy = strategyData?.data;
  const stats = statsData?.data;
  const isLoading = strategyLoading || statsLoading;

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px 0' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!strategy) {
    return (
      <Empty description={t('common:not_found')}>
        <Button type="primary" onClick={() => navigate('/strategies')}>
          {t('common:back')}
        </Button>
      </Empty>
    );
  }

  // 直接使用后端返回的统计数据
  const winRate = stats?.win_rate || 0;
  const avgPnl = stats?.avg_pnl || 0;
  const profitFactor = stats?.profit_factor || 0;
  const totalCommission = stats?.total_commission || 0;

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/strategies')}
          style={{ marginBottom: 16 }}
        >
          {t('common:back')}
        </Button>
        <h1>{strategy.name} - {t('strategy:stats.title')}</h1>
        {strategy.description && (
          <p style={{ color: '#666', marginTop: 8 }}>{strategy.description}</p>
        )}
      </div>

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={8}>
          <Card>
            <Statistic
              title={t('strategy:stats.trade_count')}
              value={stats?.trade_count || 0}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <Card>
            <Statistic
              title={t('strategy:stats.total_pnl')}
              value={stats?.total_pnl || 0}
              precision={2}
              prefix="$"
              valueStyle={{ color: (stats?.total_pnl || 0) >= 0 ? '#3f8600' : '#cf1322' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <Card>
            <Statistic
              title={t('common:commission')}
              value={totalCommission}
              precision={2}
              prefix="$"
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} sm={12} lg={8}>
          <Card>
            <Statistic
              title={t('strategy:stats.win_rate')}
              value={winRate}
              precision={1}
              suffix="%"
              valueStyle={{ color: winRate >= 50 ? '#3f8600' : '#cf1322' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <Card>
            <Statistic
              title={t('strategy:stats.avg_pnl')}
              value={avgPnl}
              precision={2}
              prefix="$"
              valueStyle={{ color: avgPnl >= 0 ? '#3f8600' : '#cf1322' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <Card>
            <Statistic
              title={t('strategy:stats.profit_factor')}
              value={profitFactor}
              precision={2}
              valueStyle={{ color: profitFactor >= 1 ? '#3f8600' : '#cf1322' }}
            />
          </Card>
        </Col>
      </Row>

      {strategy.conditions && strategy.conditions.length > 0 && (
        <Card title={t('strategy:conditions')} style={{ marginTop: 16 }}>
          <ol style={{ paddingLeft: 20 }}>
            {strategy.conditions.map((condition) => (
              <li key={condition.id} style={{ marginBottom: 8 }}>
                {condition.description}
              </li>
            ))}
          </ol>
        </Card>
      )}
    </div>
  );
}
